// ═══════════════════════════════════════════════════════════════
// PA Grants — Applicant Portal: Documents
// Filtered document list with upload, scoped to user's org
// ═══════════════════════════════════════════════════════════════

import { useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Stack, Badge, Button, TextInput, Select,
  Textarea, Modal, SimpleGrid, Table, Loader, Center, FileInput, SegmentedControl,
} from '../../../components/gab/GabComponents';
import {
  IconSearch, IconRefresh, IconUpload, IconFileDescription, IconDownload,
} from '@tabler/icons-react';
import { notifications } from '../../../lib/notifications';
import { exportToCSV } from '../../../lib/exportUtils';
import { useMyPAOrg } from '../../../hooks/useMyPAOrg';
import { useMyPADocuments, useMyPAProjects } from '../../../hooks/useMyPAOrgData';
import { usePAGrantsStore } from '../../../store/paGrantsStore';
import { useRoleStore } from '../../../store/roleStore';
import { useAuthStore } from '../../../store/authStore';
import { FIELDS, TABLES, createRecord, updateRecord } from '../../../lib/api-switch';
import { uploadFileToV2, getV2FileDownloadUrl } from '../../../lib/uploadFile';

const DOC_TYPE_OPTIONS = [
  { value: 'project_worksheet', label: 'Project Worksheet' },
  { value: 'cost_estimate', label: 'Cost Estimate' },
  { value: 'photo', label: 'Photo Documentation' },
  { value: 'insurance', label: 'Insurance Document' },
  { value: 'contract', label: 'Contract' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'correspondence', label: 'Correspondence' },
  { value: 'environmental', label: 'Environmental/Historic' },
  { value: 'other', label: 'Other' },
];

const DOC_TYPE_COLORS: Record<string, string> = {
  project_worksheet: 'var(--color-primary-700)', cost_estimate: 'var(--color-success)', photo: 'var(--data-viz-650)',
  insurance: '#F86A0B', contract: 'var(--data-viz-650)', invoice: '#03AC9E',
  correspondence: '#939598', environmental: '#08A847', other: '#939598',
};

export default function PortalDocuments() {
  const myOrg = useMyPAOrg();
  const myDocs = useMyPADocuments();
  const myProjects = useMyPAProjects();
  const { loadingDocs, fetchDocuments } = usePAGrantsStore();
  const user = useAuthStore(s => s.user);
  const effectiveRole = useRoleStore(s => s.demoRoleOverride ?? s.myRole);
  const isViewer = effectiveRole?.roleSlug === 'pa_applicant_viewer';

  const [search, setSearch] = useState('');
  const [filterProject, setFilterProject] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [showUpload, setShowUpload] = useState(false);
  const [form, setForm] = useState({
    documentName: '', documentType: '', projectId: '', description: '',
  });
  const [file, setFile] = useState<File | null>(null);

  // Data is fetched by ApplicantPortalLayout — no need to re-fetch here

  // Count docs by type for the segment badges
  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    myDocs.forEach(d => { counts[d.documentType] = (counts[d.documentType] || 0) + 1; });
    return counts;
  }, [myDocs]);

  const typeSegments = useMemo(() => [
    { value: 'all', label: `All (${myDocs.length})` },
    ...DOC_TYPE_OPTIONS.filter(t => typeCounts[t.value]).map(t => ({
      value: t.value,
      label: `${t.label} (${typeCounts[t.value] || 0})`,
    })),
  ], [myDocs.length, typeCounts]);

  const filtered = useMemo(() => {
    let list = myDocs;
    if (filterType !== 'all') {
      list = list.filter(d => d.documentType === filterType);
    }
    if (filterProject !== 'all') {
      list = list.filter(d => d.projectId === filterProject);
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(d =>
        (d.documentName || '').toLowerCase().includes(q) ||
        (d.documentType || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [myDocs, search, filterProject, filterType]);

  const projectOptions = useMemo(() =>
    myProjects.map(p => ({ value: String(p.Rid), label: `${p.projectNumber} — ${p.projectTitle}` })),
    [myProjects],
  );

  const [uploading, setUploading] = useState(false);

  async function handleUpload() {
    if (!form.documentName.trim() || !myOrg) return;
    setUploading(true);
    try {
      const FDoc = FIELDS.paDocuments;
      const fields: Array<[string, string]> = [
        [FDoc.organizationId, String(myOrg.Rid)],
        [FDoc.title, form.documentName.trim()],
        [FDoc.documentType, form.documentType],
        [FDoc.uploadedBy, user?.fullName ?? user?.email ?? ''],
        [FDoc.notes, form.description.trim()],
      ];
      if (form.projectId) fields.push([FDoc.projectId, form.projectId]);
      const rid = await createRecord(TABLES.paDocuments, fields);
      if (file) {
        const { attachmentId } = await uploadFileToV2(file, TABLES.paDocuments);
        await updateRecord(TABLES.paDocuments, rid, [[FDoc.file, attachmentId]]);
      }
      await fetchDocuments();
      notifications.show({ color: 'var(--color-success)', title: 'Uploaded', message: `Document "${form.documentName}" uploaded.` });
      setShowUpload(false);
      setForm({ documentName: '', documentType: '', projectId: '', description: '' });
      setFile(null);
    } catch {
      notifications.show({ color: 'var(--color-error)', title: 'Error', message: 'Failed to upload document.' });
    } finally {
      setUploading(false);
    }
  }

  if (loadingDocs) {
    return <Center p="xl"><Loader size="sm" /></Center>;
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={3}>Documents</Title>
        <Group gap="xs">
          <Button variant="light" size="xs" leftSection={<IconDownload size={16} />} onClick={() => {
            exportToCSV('my-pa-documents.csv',
              ['Name', 'Type', 'Project', 'Upload Date', 'Uploaded By'],
              filtered.map(d => {
                const proj = myProjects.find(p => String(p.Rid) === d.projectId);
                return [d.documentName, d.documentType, proj ? proj.projectNumber : '', d.uploadDate ? new Date(d.uploadDate).toLocaleDateString() : '', d.uploadedBy];
              })
            );
          }}>Export CSV</Button>
          <Button variant="subtle" size="xs" leftSection={<IconRefresh size={14} />}
            onClick={() => fetchDocuments()}>Refresh</Button>
          {!isViewer && (
            <Button size="xs" leftSection={<IconUpload size={14} />}
              onClick={() => setShowUpload(true)}>Upload Document</Button>
          )}
        </Group>
      </Group>

      <div style={{ overflowX: 'auto' }}>
        <SegmentedControl
          size="xs"
          data={typeSegments}
          value={filterType}
          onChange={setFilterType}
        />
      </div>

      <Group gap="xs">
        <TextInput
          placeholder="Search documents..."
          leftSection={<IconSearch size={14} />}
          size="xs" value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, maxWidth: 300 }}
        />
        <Select
          size="xs"
          data={[{ value: 'all', label: 'All Projects' }, ...projectOptions]}
          value={filterProject}
          onChange={v => setFilterProject(v ?? 'all')}
          style={{ width: 220 }}
          placeholder="Filter by project"
        />
      </Group>

      <Paper withBorder radius="md">
        <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 380px)' }}>
        <Table striped highlightOnHover>
          <Table.Thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#F8F8F8' }}>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Type</Table.Th>
              <Table.Th>Project</Table.Th>
              <Table.Th>Uploaded By</Table.Th>
              <Table.Th>Date</Table.Th>
              <Table.Th>Tags</Table.Th>
              <Table.Th></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filtered.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <Text c="dimmed" ta="center" py="md" size="sm">No documents</Text>
                </Table.Td>
              </Table.Tr>
            ) : filtered.map(d => {
              const proj = myProjects.find(p => String(p.Rid) === d.projectId);
              return (
                <Table.Tr key={d.Rid}>
                  <Table.Td>
                    <Group gap={6}>
                      <IconFileDescription size={14} color="#939598" />
                      <Text size="sm" fw={500}>{d.documentName}</Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="xs" color={DOC_TYPE_COLORS[d.documentType] || '#939598'} variant="light">
                      {d.documentType.replace(/_/g, ' ')}
                    </Badge>
                  </Table.Td>
                  <Table.Td><Text size="sm">{proj ? proj.projectNumber : '--'}</Text></Table.Td>
                  <Table.Td><Text size="sm">{d.uploadedBy}</Text></Table.Td>
                  <Table.Td><Text size="sm">{d.uploadDate ? new Date(d.uploadDate).toLocaleDateString() : '--'}</Text></Table.Td>
                  <Table.Td><Text size="xs" c="dimmed">{d.tags || '--'}</Text></Table.Td>
                  <Table.Td>
                    {d.fileId && (
                      <Button size="xs" variant="subtle" leftSection={<IconDownload size={12} />}
                        onClick={async () => {
                          try {
                            const url = await getV2FileDownloadUrl(d.fileId);
                            window.open(url, '_blank', 'noopener,noreferrer');
                          } catch { notifications.show({ message: 'Download failed', color: 'var(--color-error)' }); }
                        }}>Download</Button>
                    )}
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
        </div>
      </Paper>

      {/* Upload modal */}
      <Modal opened={showUpload} onClose={() => setShowUpload(false)} title="Upload Document" size="lg">
        <Stack gap="sm">
          <TextInput label="Document Name" required value={form.documentName}
            onChange={e => setForm(f => ({ ...f, documentName: e.target.value }))} />
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <Select label="Document Type" data={DOC_TYPE_OPTIONS}
              value={form.documentType || null} onChange={v => setForm(f => ({ ...f, documentType: v ?? '' }))} />
            <Select label="Link to Project" data={projectOptions}
              value={form.projectId || null} onChange={v => setForm(f => ({ ...f, projectId: v ?? '' }))}
              placeholder="Optional" searchable clearable />
          </SimpleGrid>
          <Textarea label="Description" value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            rows={2} />
          <FileInput
            label="File"
            placeholder="Select file to upload"
            value={file}
            onChange={setFile}
            accept="application/pdf,.docx,.xlsx,.pptx,image/*,.zip"
          />
          <Text size="xs" c="dimmed">
            Accepted: PDF, DOCX, XLSX, PPTX, images, ZIP. No .txt files.
          </Text>
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={() => setShowUpload(false)}>Cancel</Button>
            <Button onClick={handleUpload} loading={uploading} leftSection={<IconUpload size={16} />}>Upload</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
